import React, { useState, useContext, useEffect } from "react";
import { Box, Button, Grid2, Typography } from "@mui/material";
import { PermissionsContext } from "../permission-context";
import unauth from "../images/unauth.png";
import { showErrorToast, showInfoToast, showSuccessToast } from "../helpers/utils";
import { pca } from "../msalConfig";
import uploadService from "../api/services/upload-service";
import WciOrthoSurgeryVisitModel from "../models/wci-ortho-surgery-visits-model";
import { parse, format } from "date-fns";
import UploadHistoryTable from "../components/upload-history-table-antd";
import UploadHistoryService from "../api/services/upload-history";
import { ReactComponent as FileUploadIconActive } from "../Icons/vuesax/document-upload-active.svg";
import { ReactComponent as FileUploadIconDisabled } from "../Icons/vuesax/document-upload-disabled.svg";

function WciOrthoSurgeryVisit() {
  const { loading, error, hasPermission } = useContext(PermissionsContext);
  const [isUploading, setIsUploading] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const user = pca && pca.getActiveAccount() ? pca.getActiveAccount().username : "";

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await UploadHistoryService.getHistory(1);
      if (res.status === 200 || res.status === 201) {
        setHistoryData(res.data);
      } else {
        showErrorToast(
          "Unable to fetch accounts. Please check your connection or try again later."
        );
        setHistoryData(null);
      }
    } catch (error) {
      showErrorToast("Error fetching Upload History:", error);
      setHistoryData(null);
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const fileName = selectedFile.name;
    const fileExtension = fileName.split(".").pop().toLowerCase();
    if (fileExtension !== "csv") {
      showErrorToast("Only CSV files are allowed.");
      return;
    }

    const reader = new FileReader();
    reader.readAsText(selectedFile);
    reader.onload = async function (e) {
      const csv = e.target.result;
      const rows = csv.split("\n");
      if (rows.length > 0) {
        const header = rows[0].trim().split(",");
        if (!header.includes("PATNUM") || !header.includes("ADMDT")) {
          var message = "";
          if (!header.includes("PATNUM")) {
            message = "File Missing column PATNUM";
          } else if (!header.includes("ADMDT")) {
            message = "File Missing column ADMDT";
          }
          showErrorToast(message);
          await uploadFileWithException(message, fileName);
          document.getElementById("file-upload").value = "";
          return;
        }

        const patNumIndex = header.indexOf("PATNUM");
        const admDateIndex = header.indexOf("ADMDT");
        const invalidRows = [];
        const dateRegex = /^(0[1-9]|1[0-2])([0-2][0-9]|3[0-1])\d{2}$/;

        const lastRow = rows[rows.length - 1];
        const columns = lastRow.split(",");

        if (!columns[patNumIndex] || !columns[admDateIndex]) {
          rows.pop();
        }
        const surgeryVisits = rows
          .slice(1)
          .filter((row, index) => {
            const columns = row.split(",");
            if (!columns[patNumIndex] || !columns[admDateIndex]) {
              invalidRows.push(index + 1);
              return false;
            }

            if (!dateRegex.test(columns[admDateIndex])) {
              invalidRows.push(index + 1);
              return false;
            }
            return true;
          })
          .map((row) => {
            const columns = row.split(",");
            return new WciOrthoSurgeryVisitModel(columns[patNumIndex], columns[admDateIndex]);
          });
        setIsUploading(true);
        await uploadFileToServer(surgeryVisits, invalidRows, fileName);
        setIsUploading(false);
        document.getElementById("file-upload").value = "";
      }
    };
  };
  const uploadFileWithException = async (exceptionMessage, fileName) => {
    var visits = [];
    const requestData = {
      wciOrthoSurgeryVisits: visits,
      invalidRowInfo: exceptionMessage,
      fileName: fileName,
      userId: user,
    };
    try {
      await uploadService.uploadJobs(requestData);
    } catch {
    } finally {
      setHistoryData(null);
      fetchHistory();
    }
  };
  const uploadFileToServer = async (csvData, invalidRows, fileName) => {
    const transformedData = csvData.map((visit) => {
      if (visit.dateOfProcedure) {
        const parsedDate = parse(visit.dateOfProcedure, "MMddyy", new Date());
        visit.dateOfProcedure = format(parsedDate, "yyyy-MM-dd");
      }
      return visit;
    });
    let invalidRowDetails = invalidRows.length > 0 ? invalidRows.join(", ") : "";
    const requestData = {
      wciOrthoSurgeryVisits: transformedData,
      invalidRowInfo: invalidRowDetails,
      fileName: fileName,
      userId: user,
    };
    try {
      await uploadService.uploadJobs(requestData);
      if (invalidRowDetails.length > 0) {
        showInfoToast(
          "File partially uploaded. " +
            `Unable to read data from rows: ${invalidRows.join(
              ", "
            )}. Please check these rows and try again.`
        );
      } else {
        showSuccessToast("File uploaded successfully!");
      }
    } catch (error) {
      if (error.response) {
        showErrorToast("Upload failed");
      } else {
        showErrorToast("Upload failed");
      }
    } finally {
      setHistoryData(null);
      fetchHistory();
    }
  };
  return (
    <>
      {!loading && !error && hasPermission("PatientVisits") && (
        <Box style={{ marginRight: "1%", marginLeft: "2%" }}>
          <Grid2
            container
            justifyContent='space-between'
            alignItems='center'
            sx={{
              borderBottom: 1,
              borderColor: "#dfdfdf",
              marginTop: 1.5,
            }}>
            <Grid2>
              <Typography
                gutterBottom
                fontWeight='700'
                lineHeight={"31.25px"}
                fontSize={"24px"}
                fontStyle={"DM Sans"}>
                WCI OrthoSurgery Visits Update
              </Typography>
            </Grid2>
            <Grid2
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
                transform: "translateY(-4px)",
              }}>
              <form
                onSubmit={(e) => e.preventDefault()}
                className='file-upload-form'
                style={{ marginLeft: "2px" }}>
                <input
                  accept='.csv'
                  id='file-upload'
                  type='file'
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <label htmlFor='file-upload'>
                  <Button
                    variant='contained'
                    component='span'
                    startIcon={
                      isUploading ? (
                        <FileUploadIconDisabled
                          width='1em'
                          height='1em'
                          style={{ fontSize: "22px" }}
                        />
                      ) : (
                        <FileUploadIconActive
                          width='1em'
                          height='1em'
                          style={{ fontSize: "22px" }}
                        />
                      )
                    }
                    type='submit'
                    disabled={isUploading}
                    sx={{ minWidth: "144px" }}>
                    Upload CSV
                  </Button>
                </label>
              </form>
            </Grid2>
          </Grid2>
          <Grid2 size={12}>
            <UploadHistoryTable data={historyData} tableName='wciOrthoSurgeryVisit' />
          </Grid2>
        </Box>
      )}
      {!loading && !error && !hasPermission("PatientVisits") && (
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "90vh",
          }}>
          <img src={unauth} alt='UnAuthorized' style={{ maxWidth: "100%", maxHeight: "100%" }} />
        </Box>
      )}
    </>
  );
}

export default WciOrthoSurgeryVisit;
